import { useEffect, useMemo } from 'react';
import { SocketEventType } from 'types/socket';
import { useSignalR } from './useSignalR';

/**
 * Hook for registering socket event listeners.
 *
 * @param connection - The SignalR HubConnection or null.
 * @param events - An array of event names to register.
 * @param onSocketNotif - Callback function that is invoked for each event.
 */
export function useSocketListeners(
  events: Array<SocketEventType>,
  onSocketNotif: (type: SocketEventType, message: string | null | any) => void,
) {
  const { connection } = useSignalR();
  // Memoize the handlers to ensure their references remain stable.
  const socketHandlers = useMemo(() => {
    const handlers: Record<string, (message: any) => void> = {};
    events.forEach((eventName) => {
      handlers[eventName] = (message: any) => {
        // אם ההודעה היא אובייקט, נשלח אותה ישירות, אחרת נשלח אותה כמחרוזת
        onSocketNotif(eventName, typeof message === 'string' ? message : message);
      };
    });
    return handlers;
  }, [events, onSocketNotif]);

  useEffect(() => {
    if (!connection) return;

    // Register the handlers for each event
    events.forEach((eventName) => {
      connection.on(eventName, socketHandlers[eventName]);
    });

    // Cleanup: Unregister the handlers when the component unmounts or dependencies change.
    return () => {
      events.forEach((eventName) => {
        connection.off(eventName, socketHandlers[eventName]);
      });
    };
  }, [connection, events, socketHandlers]);
}
